/** network = no response (offline, timeout); server = 5xx; client = 4xx; unknown = anything else. */
export type ApiErrorKind = "network" | "server" | "client" | "unknown";
/** `message` is the server's own text and is only set for 4xx. For network/5xx/unknown it is "" (never axios
 *  English): show `messageKey(e)` translated instead — or just use applyServerErrors, which does both. */
/** `count` is a numeric `count` in the error body (e.g. the server's fresh count on a 409 CONFIRM_COUNT_MISMATCH). */
export type ApiError = { status: number | null; kind: ApiErrorKind; message: string; fieldErrors: Record<string, string>; code?: string; count?: number; raw: unknown };

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

/** One shape for every failure: Laravel 422 field errors, coded refusals, 5xx, network. */
export const toApiError = (err: unknown): ApiError => {
  const e = err as any;
  const data = e?.response?.data;
  const fieldErrors: Record<string, string> = {};
  if (data?.errors && typeof data.errors === "object") {
    for (const [k, v] of Object.entries(data.errors)) {
      const first = Array.isArray(v) ? text(v[0]) : text(v);
      if (first) fieldErrors[k] = first;
    }
  }
  const status: number | null = typeof e?.response?.status === "number" ? e.response.status : null;
  const kind: ApiErrorKind = status === null ? (e?.isAxiosError ? "network" : "unknown") : status >= 500 ? "server" : status >= 400 ? "client" : "unknown";
  return {
    status,
    kind,
    message: kind === "client" ? text(data?.message) ?? "" : "",
    fieldErrors,
    code: text(data?.code) ?? undefined,
    ...(typeof data?.count === "number" && Number.isFinite(data.count) ? { count: data.count } : {}),
    raw: err,
  };
};

/** Translation key (under `next.`) for errors that carry no readable server message; null when `message` is fit to show. */
export const messageKey = (e: ApiError): string | null =>
  e.message ? null : e.kind === "network" ? "errors.network" : e.kind === "server" ? "errors.server" : "errors.generic";

export const isApiError = (e: unknown): e is ApiError =>
  !!e && typeof e === "object" && "fieldErrors" in (e as object) && "message" in (e as object);

/**
 * Stage 5A: every refusal code the access endpoints answer with ({message, code}). Each has a plain sentence at
 * `next.access.errors.<code lower-case>` in every language; the server's own text is English, for logs, never shown.
 */
export const ACCESS_ERROR_CODES = [
  "NOT_ALLOWED", "VIEWING_AS_BLOCKED", "VIEWING_AS_LOOK_ONLY", "NOT_VIEWING_AS", "HANDOVER_NOT_YOURS", "CHANGES_MANAGERS_ONLY", "VIEW_AS_ENDED",
  "HANDOVER_NOT_FOUND", "ACCOUNT_NOT_FOUND", "MEMBER_NOT_FOUND", "USER_NOT_FOUND", "SET_NOT_FOUND",
  "ACCESS_CHANGED", "ACCESS_CONFIG_CHANGED", "ACCESS_CONFIG_BUSY", "HANDOVER_PENDING", "HANDOVER_NOT_PENDING", "TEAM_MOVED",
  "ACCESS_MODEL_LEGACY", "ALREADY_MEMBER", "OWNER_ALREADY_CONFIRMED", "OWNER_NOT_LISTED", "OWNER_NOT_CONFIRMED", "VIEW_AS_KEY_USED",
  "OWNER_PROTECTED", "OWN_ACCESS", "OWNER_ONLY_BY_HANDOVER", "MANAGERS_OWNER_ONLY", "OWNER_NO_END_DATE", "OWNER_MUST_HAND_OVER",
  "UNKNOWN_SWITCH", "REACH_EMPTY", "REACH_NOT_IN_ACCOUNT", "NO_TEAM_IN_PERSONAL_ACCOUNT", "INVALID_CHANGE", "HANDOVER_TARGET_INVALID",
  "UNKNOWN_PERMISSION", "UNKNOWN_PRESET", "UNKNOWN_ACCOUNT_TYPE", "UNKNOWN_PLAN", "NOT_A_SWITCH", "SWITCH_IN_ROLE_LIST", "VISITS_TOGETHER",
  "OWNER_KEEPS_ALL", "OWNER_CANNOT_LEAVE", "OWNER_ONLY_PERMISSION", "NOT_FOR_VOLUNTEERS", "NOT_FOR_TEAM_MEMBERS",
] as const;

const ACCESS_CODES = new Set<string>(ACCESS_ERROR_CODES);

/** Translation key (under `next.`) for a 5A code; null for any other error. */
export const accessCodeKey = (code: string | undefined): string | null =>
  code && ACCESS_CODES.has(code) ? `access.errors.${code.toLowerCase()}` : null;
